import React, { useEffect, useState, useCallback } from "react";
import api from "./api";
import SiteTable from "./components/SiteTable";
import SiteDetails from "./components/SiteDetails";
import LoginForm from "./components/LoginForm";
import AdminPanel from "./components/AdminPanel";
import ThemeToggle from "./components/ThemeToggle";

function App() {
    const [sites, setSites] = useState([]);
    const [selectedSite, setSelectedSite] = useState(null);
    const [loadingSites, setLoadingSites] = useState(true);
    const [errorSites, setErrorSites] = useState("");

    const [auth, setAuth] = useState({
        token: null,
        role: null,
        email: null
    });
    const [authChecked, setAuthChecked] = useState(false);
    const [showAdminPanel, setShowAdminPanel] = useState(false);

    const [scope, setScope] = useState("all");
    const [currentFolderId, setCurrentFolderId] = useState(null);
    const [currentFolder, setCurrentFolder] = useState(null);
    const [breadcrumbs, setBreadcrumbs] = useState([]);

    const isAdmin = auth.role === "admin";
    const isAdminPath = window.location.pathname.startsWith("/admin");

    const [isMobileView, setIsMobileView] = useState(window.innerWidth < 768);

    useEffect(() => {
        const handleResize = () => {
            const isMobile = window.innerWidth < 768;
            setIsMobileView(isMobile);
        };

        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    const fetchBreadcrumbs = useCallback(async (folderId) => {
        if (folderId === null || folderId === undefined) {
            setBreadcrumbs([]);
            setCurrentFolder(null);
            return;
        }

        try {
            let res;

            if (isAdminPath && isAdmin) {
                res = await api.get(`/sites/${folderId}/breadcrumbs`, {
                    headers: {
                        Authorization: `Bearer ${auth.token}`
                    }
                });
            } else {
                res = await api.get(`/public/sites/${folderId}/breadcrumbs`);
            }

            const crumbs = Array.isArray(res.data) ? res.data : [];
            setBreadcrumbs(crumbs);

            const exactCurrent = crumbs.find(
                (item) => String(item.id) === String(folderId)
            );
            setCurrentFolder(exactCurrent || null);
        } catch (err) {
            console.error("fetchBreadcrumbs error:", err);
            setBreadcrumbs([]);
            setCurrentFolder(null);
        }
    }, [isAdminPath, isAdmin, auth.token]);

    const fetchSites = useCallback(async () => {
        setLoadingSites(true);
        setErrorSites("");

        try {
            let res;

            const parentParam = currentFolderId === null ? "null" : currentFolderId;

            if (isAdminPath && isAdmin) {
                res = await api.get("/sites", {
                    headers: {
                        Authorization: `Bearer ${auth.token}`
                    },
                    params: {
                        scope,
                        parentId: parentParam
                    }
                });
            } else {
                res = await api.get("/public/sites", {
                    params: {
                        parentId: parentParam
                    }
                });
            }

            const data = Array.isArray(res.data) ? res.data : [];

            setSites(data);

            setSelectedSite((prev) => {
                if (!data.length) return null;
                if (!prev) return data[0];

                const stillExists = data.find((s) => s.id === prev.id);
                return stillExists || data[0];
            });

            await fetchBreadcrumbs(currentFolderId);
        } catch (err) {
            console.error("fetchSites error:", err);
            setErrorSites("Failed to load site list.");
        } finally {
            setLoadingSites(false);
        }
    }, [isAdminPath, isAdmin, scope, auth.token, currentFolderId, fetchBreadcrumbs]);

    useEffect(() => {
        if (!isAdminPath) {
            fetchSites();
        }
    }, [isAdminPath, fetchSites]);

    useEffect(() => {
        if (isAdminPath && isAdmin) {
            fetchSites();
        }
    }, [isAdminPath, isAdmin, scope, currentFolderId, fetchSites]);

    useEffect(() => {
        if (!isAdminPath) {
            setAuthChecked(true);
            return;
        }

        const token = localStorage.getItem("token");

        if (!token) {
            setAuthChecked(true);
            return;
        }

        api
            .get("/auth/me", {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            })
            .then((res) => {
                const { user } = res.data || {};
                if (!user) {
                    throw new Error("No user in /auth/me response");
                }

                const { email, role } = user;

                setAuth({ token, email, role });
                localStorage.setItem("role", role);
                localStorage.setItem("email", email);
            })
            .catch((err) => {
                console.error("Token invalid:", err);
                localStorage.removeItem("token");
                localStorage.removeItem("role");
                localStorage.removeItem("email");
                setAuth({ token: null, role: null, email: null });
            })
            .finally(() => {
                setAuthChecked(true);
            });
    }, [isAdminPath]);

    const loadingSpinner = () => (
        <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-950">
            <div className="flex flex-col items-center gap-3 text-slate-500 dark:text-slate-400">
                <div className="h-6 w-6 animate-spin rounded-full border-2 border-slate-300 border-t-slate-900 dark:border-slate-700 dark:border-t-slate-200" />
                <p>Access verification…</p>
            </div>
        </div>
    );

    const handleLogin = ({ token, role, email }) => {
        setAuth({ token, role, email });
        localStorage.setItem("token", token);
        localStorage.setItem("role", role);
        localStorage.setItem("email", email);
    };

    const handleLogout = () => {
        localStorage.removeItem("token");
        localStorage.removeItem("role");
        localStorage.removeItem("email");
        setAuth({ token: null, role: null, email: null });
        setShowAdminPanel(false);
    };

    const openFolder = async (folder) => {
        if (!folder || folder.type !== "folder") return;

        setCurrentFolderId(folder.id);
        setCurrentFolder(folder);
        setSelectedSite(null);
    };

    const goToRoot = () => {
        setCurrentFolderId(null);
        setCurrentFolder(null);
        setSelectedSite(null);
        setBreadcrumbs([]);
    };

    const goToBreadcrumb = (crumb) => {
        if (!crumb) {
            goToRoot();
            return;
        }

        setCurrentFolderId(crumb.id);
        setCurrentFolder(crumb);
        setSelectedSite(null);
    };

    const handleSelectSite = (site) => {
        if (!site) return;

        if (site.type === "folder") {
            openFolder(site);
            return;
        }

        setSelectedSite(site);
    };

    const handleReorderSites = async (newOrderedSites) => {
        setSites(newOrderedSites);

        if (!isAdminPath || !isAdmin) return;

        try {
            const payload = newOrderedSites.map((site, index) => ({
                id: site.id,
                priority: index + 1
            }));

            await api.put("/sites/priorities", payload, {
                headers: {
                    Authorization: `Bearer ${auth.token}`
                }
            });
        } catch (err) {
            console.error("Failed to save order:", err);
            setErrorSites("Failed to save order.");
            fetchSites();
        }
    };

    const handleMoveToFolder = async (sourceItem, targetFolderId) => {
        if (!isAdminPath || !isAdmin || !sourceItem) return;

        try {
            await api.patch(
                `/sites/${sourceItem.id}/move`,
                { parentId: targetFolderId },
                {
                    headers: {
                        Authorization: `Bearer ${auth.token}`
                    }
                }
            );

            if (selectedSite?.id === sourceItem.id) {
                setSelectedSite(null);
            }

            fetchSites();
        } catch (err) {
            console.error("Failed to move item to folder:", err);
            setErrorSites(err.response?.data?.message || "Failed to move item.");
        }
    };

    const handleMoveToRoot = async (sourceItem) => {
        if (!isAdminPath || !isAdmin || !sourceItem) return;

        try {
            await api.patch(
                `/sites/${sourceItem.id}/move`,
                { parentId: null },
                {
                    headers: {
                        Authorization: `Bearer ${auth.token}`
                    }
                }
            );

            if (selectedSite?.id === sourceItem.id) {
                setSelectedSite(null);
            }

            fetchSites();
        } catch (err) {
            console.error("Failed to move item to root:", err);
            setErrorSites(err.response?.data?.message || "Failed to move item.");
        }
    };

    if (isAdminPath) {
        if (!authChecked) {
            return loadingSpinner();
        }

        if (!auth.token) {
            return <LoginForm onLogin={handleLogin} />;
        }

        if (!isAdmin) {
            return (
                <div className="min-h-screen flex flex-col">
                    <header>...</header>
                    <main>... (use the public interface) ...</main>
                </div>
            );
        }
    }

    return (
        <div className="h-screen flex flex-col bg-slate-50 text-slate-900 dark:bg-slate-950 dark:text-slate-50 transition-colors">
            <header className="shrink-0 border-b border-slate-200/80 bg-white/80 backdrop-blur-md dark:border-slate-800/80 dark:bg-slate-950/80">
                <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-3 md:py-4">
                    <div className="space-y-1">
                        <h1 className="text-xl md:text-2xl font-semibold tracking-tight text-slate-900 dark:text-slate-50">
                            Catalog of developed websites
                        </h1>
                        <p className="text-xs md:text-sm text-slate-500 dark:text-slate-400">
                            On the left is a list of projects and folders. On the right is a detailed description.
                        </p>
                    </div>

                    <div className="flex items-center gap-3">
                        <ThemeToggle />

                        {isAdminPath && auth.token && (
                            <div className="flex items-center gap-2 rounded-full border border-slate-200/80 bg-white/70 px-3 py-1 text-xs shadow-sm dark:border-slate-700/80 dark:bg-slate-900/80">
                                {auth.email && (
                                    <p className="text-[11px] text-slate-600 dark:text-slate-300 truncate max-w-[160px]">
                                        {auth.email}
                                    </p>
                                )}

                                {isAdmin && (
                                    <>
                                        <button
                                            type="button"
                                            onClick={() => setShowAdminPanel(true)}
                                            className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-[11px] font-medium text-slate-700 hover:bg-slate-100 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700 transition-colors"
                                        >
                                            Edit
                                        </button>

                                        <button
                                            onClick={handleLogout}
                                            className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-[11px] font-medium text-slate-700 hover:bg-slate-100 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700 transition-colors"
                                        >
                                            Log out
                                        </button>
                                    </>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </header>

            <main className="flex-1 flex justify-center overflow-hidden">
                {loadingSites ? (
                    <div className="flex flex-1 items-center justify-center">
                        <div className="flex flex-col items-center gap-3 text-slate-500 dark:text-slate-400">
                            <div className="h-6 w-6 animate-spin rounded-full border-2 border-slate-300 border-t-slate-900 dark:border-slate-700 dark:border-t-slate-200" />
                            <p>Uploading projects…</p>
                        </div>
                    </div>
                ) : errorSites ? (
                    <div className="flex flex-1 items-center justify-center">
                        <p className="text-red-500 text-sm">{errorSites}</p>
                    </div>
                ) : (
                    <>
                        {isMobileView ? (
                            <div className="flex h-full w-full max-w-6xl px-4 py-4 md:py-6 overflow-y-auto">
                                <div className="flex flex-col gap-4 w-full">
                                    <section className="w-full">
                                        <div className="rounded-3xl border border-slate-200 bg-white/90 shadow-soft transition-all dark:border-slate-800 dark:bg-slate-900/90 overflow-hidden">
                                            <div className="max-h-[50vh] overflow-y-auto p-2">
                                                <SiteTable
                                                    sites={sites}
                                                    selectedSite={selectedSite}
                                                    onSelect={handleSelectSite}
                                                    isAdmin={isAdminPath && isAdmin}
                                                    scope={isAdminPath && isAdmin ? scope : undefined}
                                                    onScopeChange={isAdminPath && isAdmin ? setScope : undefined}
                                                    onReorder={isAdminPath && isAdmin ? handleReorderSites : undefined}
                                                    currentFolderId={currentFolderId}
                                                    currentFolder={currentFolder}
                                                    breadcrumbs={breadcrumbs}
                                                    onMoveToFolder={isAdminPath && isAdmin ? handleMoveToFolder : undefined}
                                                    onMoveToRoot={isAdminPath && isAdmin ? handleMoveToRoot : undefined}
                                                    goToBreadcrumb={goToBreadcrumb}
                                                    goToRoot={goToRoot}
                                                />
                                            </div>
                                        </div>
                                    </section>

                                    <section className="w-full">
                                        <div className="rounded-3xl border border-slate-200 bg-white/90 backdrop-blur-xl dark:border-slate-800 dark:bg-slate-900/90 shadow-sm flex flex-col p-2 relative">
                                            {selectedSite && selectedSite.type !== "folder" && selectedSite.siteUrl && (
                                                <div className="flex justify-end mb-2">
                                                    <a
                                                        href={selectedSite.siteUrl}
                                                        target="_blank"
                                                        rel="noreferrer"
                                                        className="inline-flex items-center gap-2 rounded-full bg-slate-900 px-4 py-2 text-xs font-medium text-slate-50 shadow-sm hover:bg-black dark:bg-slate-100 dark:text-slate-900 dark:hover:bg-white transition-colors"
                                                    >
                                                        Open site
                                                        <span aria-hidden="true">↗</span>
                                                    </a>
                                                </div>
                                            )}

                                            <div className="max-h-[50vh] overflow-y-auto p-2 pt-0">
                                                <SiteDetails site={selectedSite} />
                                            </div>
                                        </div>
                                    </section>
                                </div>
                            </div>
                        ) : (
                            <div className="flex h-full w-full max-w-6xl gap-4 px-4 py-4 md:py-6 md:flex-row flex-col overflow-hidden">
                                <section className="w-full md:w-5/12 h-[calc(100vh-9rem)]">
                                    <div className="rounded-3xl border border-slate-200 bg-white/90 shadow-soft transition-all dark:border-slate-800 dark:bg-slate-900/90 overflow-hidden">
                                        <div className="max-h-[calc(100vh-9rem)] overflow-y-auto p-2">
                                            <SiteTable
                                                sites={sites}
                                                selectedSite={selectedSite}
                                                onSelect={handleSelectSite}
                                                isAdmin={isAdminPath && isAdmin}
                                                scope={isAdminPath && isAdmin ? scope : undefined}
                                                onScopeChange={isAdminPath && isAdmin ? setScope : undefined}
                                                onReorder={isAdminPath && isAdmin ? handleReorderSites : undefined}
                                                currentFolderId={currentFolderId}
                                                currentFolder={currentFolder}
                                                breadcrumbs={breadcrumbs}
                                                onMoveToFolder={isAdminPath && isAdmin ? handleMoveToFolder : undefined}
                                                onMoveToRoot={isAdminPath && isAdmin ? handleMoveToRoot : undefined}
                                                goToBreadcrumb={goToBreadcrumb}
                                                goToRoot={goToRoot}
                                            />
                                        </div>
                                    </div>
                                </section>

                                <section className="w-full md:w-7/12">
                                    <div className="rounded-3xl border border-slate-200 bg-white/90 backdrop-blur-xl dark:border-slate-800 dark:bg-slate-900/90 shadow-sm h-[calc(100vh-10rem)] flex flex-col p-2 relative">
                                        <div className="max-h-[calc(100vh-9rem)] overflow-y-auto p-2">
                                            {selectedSite && selectedSite.type !== "folder" && selectedSite.siteUrl && (
                                                <div className="absolute top-6 right-8 z-10">
                                                    <a
                                                        href={selectedSite.siteUrl}
                                                        target="_blank"
                                                        rel="noreferrer"
                                                        className="inline-flex items-center gap-2 rounded-full bg-slate-900 px-4 py-2 text-xs font-medium text-slate-50 shadow-sm hover:bg-black dark:bg-slate-100 dark:text-slate-900 dark:hover:bg-white transition-colors"
                                                    >
                                                        Open site
                                                        <span aria-hidden="true">↗</span>
                                                    </a>
                                                </div>
                                            )}

                                            <div className="h-full overflow-y-auto pl-3 pt-6 pb-10">
                                                <SiteDetails site={selectedSite} />
                                            </div>
                                        </div>
                                    </div>
                                </section>
                            </div>
                        )}
                    </>
                )}
            </main>

            {isAdminPath && isAdmin && showAdminPanel && (
                <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 backdrop-blur-sm">
                    <div className="w-full max-w-3xl rounded-3xl border border-slate-200 bg-white/95 p-6 shadow-soft dark:border-slate-700 dark:bg-slate-900/95">
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-50">
                                Editing sites and folders
                            </h2>
                            <button
                                type="button"
                                onClick={() => setShowAdminPanel(false)}
                                className="text-slate-400 hover:text-slate-600 dark:text-slate-500 dark:hover:text-slate-300 transition"
                            >
                                ✕
                            </button>
                        </div>

                        <AdminPanel
                            selectedSite={selectedSite}
                            currentFolderId={currentFolderId}
                            onAfterChange={fetchSites}
                        />
                    </div>
                </div>
            )}
        </div>
    );
}

export default App;